'use client';

import { useTranslation } from 'react-i18next';

import { ItemCardSkeleton } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { TooltipInfo } from '@sola/ark-core/molecules/tooltip-info/tooltip-info.tsx';
import { useSupporterContacts } from '@sola/supporter-sdk/hooks/settings.ts';

import { ContactCard } from '../../molecules/contact-card/contact-card.tsx';
import { LocalAlertsProvider } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';

const ContactsSettingsSkeleton = () => {
  const { t } = useTranslation('my-settings');

  return (
    <div
      aria-busy="true"
      aria-label={t('my-settings:contactSettings.loadingContactSettings')}
      className="flex flex-col gap-comfy-xl max-w-md"
    >
      <ItemCardSkeleton />
    </div>
  );
};

export const ContactsSettings = () => {
  const { contacts, showSkeleton } = useSupporterContacts();
  const { t } = useTranslation('my-settings');
  if (showSkeleton) {
    return <ContactsSettingsSkeleton />;
  }

  const primaryContact = contacts?.find((c) => c.isPrimary);
  const otherContacts = contacts?.filter((c) => !c.isPrimary) ?? [];

  return (
    <LocalAlertsProvider>
      <div className="flex flex-col gap-comfy-xl max-w-md">
        <LocalAlerts />
        {primaryContact && (
          <div className="flex flex-col gap-comfy-lg">
            <div className="flex items-center gap-snug-lg">
              <h2 className="label-neutral-potent label-potent label-md">
                {t('my-settings:contactSettings.primaryContactTitle')}
              </h2>
              <TooltipInfo
                size="sm"
                side="top"
                align="start"
                defaultWrap
                usePortal
                contentClassName="max-w-xs"
              >
                <p>{t('my-settings:contactSettings.primaryContactTooltip')}</p>
              </TooltipInfo>
            </div>
            <ContactCard contact={primaryContact} />
          </div>
        )}

        {otherContacts?.length > 0 && (
          <div className="flex flex-col gap-comfy-lg">
            <div className="flex items-center gap-snug-lg">
              <h2 className="label-neutral-potent label-potent label-md">
                {t('my-settings:contactSettings.otherContactsTitle')}
              </h2>
              <TooltipInfo
                size="sm"
                side="top"
                align="start"
                usePortal
                defaultWrap
                contentClassName="max-w-xs"
              >
                <p>{t('my-settings:contactSettings.otherContactsTooltip')}</p>
              </TooltipInfo>
            </div>

            <div className="flex flex-col gap-comfy-lg">
              {otherContacts.map((contact) => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          </div>
        )}
      </div>
    </LocalAlertsProvider>
  );
};
